import { Menu as BaseMenu } from '@base-ui/react/menu'
import { useId, type ReactElement, type ReactNode } from 'react'
import { cx } from '../cx.js'
import styles from './Menu.module.scss'

/**
 * Menu — the card's overflow of section actions on narrow widths (M3.6b).
 *
 * Wraps `@base-ui/react` Menu directly: the trigger gets `aria-haspopup="menu"`
 * and `aria-expanded`, the popup `role="menu"`, and Base UI supplies roving
 * focus, typeahead, Home/End, close on selection and on Escape, and focus
 * return to the trigger. This file adds the anatomy — items with an optional
 * leading glyph, groups with labels, separators, disabled items that say why
 * — and the one motion allowed: the popup appearing and leaving as an opacity
 * change at the fast token duration, instant under reduced motion.
 *
 * There is no red. A destructive item (Dismiss, Drop) is a reversible repair
 * and reads as one: the consumer places it last, after a separator, and
 * `destructive` sets it in medium weight. See docs/visual-direction.md.
 */
export type MenuProps = {
  /** The element that opens the menu. It must forward props to a DOM element. */
  trigger: ReactElement
  /** Accessible name of the menu itself, when the trigger's text is not it. */
  label?: string | undefined
  /** Which edge of the trigger the popup aligns to. Overflow menus sit at the end. */
  align?: 'start' | 'center' | 'end' | undefined
  side?: 'top' | 'bottom' | undefined
  open?: boolean | undefined
  defaultOpen?: boolean | undefined
  onOpenChange?: ((open: boolean) => void) | undefined
  children: ReactNode
}

export function Menu({
  trigger,
  label,
  align = 'end',
  side = 'bottom',
  open,
  defaultOpen,
  onOpenChange,
  children,
}: MenuProps) {
  return (
    <BaseMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <BaseMenu.Trigger render={trigger} />
      <BaseMenu.Portal>
        <BaseMenu.Positioner className={styles.positioner} side={side} align={align}>
          <BaseMenu.Popup
            aria-label={label}
            className={(state) =>
              cx(
                styles.popup,
                state.transitionStatus === 'starting' && styles.isEntering,
                state.transitionStatus === 'ending' && styles.isLeaving,
              )
            }
          >
            {children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  )
}

export type MenuItemProps = {
  /** The item's label. Keep it to the verb the section action uses. */
  children: ReactNode
  /** A leading glyph. Decorative: the label carries the meaning. */
  icon?: ReactNode
  onSelect?: (() => void) | undefined
  disabled?: boolean | undefined
  /** Why the item is disabled. Announced as the item's description. */
  reason?: string | undefined
  /** A reversible discard (Dismiss, Drop): last in the menu, after a separator. */
  destructive?: boolean | undefined
  /** Keep the menu open after selection. */
  closeOnClick?: boolean | undefined
}

export function MenuItem({
  children,
  icon,
  onSelect,
  disabled,
  reason,
  destructive,
  closeOnClick,
}: MenuItemProps) {
  const labelId = useId()
  const reasonId = useId()
  const describe = disabled === true && reason !== undefined
  return (
    <BaseMenu.Item
      className={(state) =>
        cx(
          styles.item,
          state.highlighted && styles.isHighlighted,
          state.disabled && styles.isDisabled,
          destructive === true && styles.isDestructive,
        )
      }
      disabled={disabled}
      onClick={onSelect}
      closeOnClick={closeOnClick}
      aria-labelledby={labelId}
      aria-describedby={describe ? reasonId : undefined}
    >
      {icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className={styles.text}>
        <span id={labelId}>{children}</span>
        {describe ? (
          <span id={reasonId} className={styles.reason}>
            {reason}
          </span>
        ) : null}
      </span>
    </BaseMenu.Item>
  )
}

export type MenuGroupProps = {
  label: string
  children: ReactNode
}

export function MenuGroup({ label, children }: MenuGroupProps) {
  return (
    <BaseMenu.Group className={styles.group}>
      <BaseMenu.GroupLabel className={styles.groupLabel}>{label}</BaseMenu.GroupLabel>
      {children}
    </BaseMenu.Group>
  )
}

export function MenuSeparator() {
  return <BaseMenu.Separator className={styles.separator} />
}
